import React, { useEffect, useState, useCallback } from "react";
import { api, PROD_STATUS_LABEL } from "@/lib/api";
import { PageHeader, Table, Btn, Modal, Input, Select, Badge, Card, Textarea } from "@/components/UI";
import { Factory, Scale, CheckCircle, Beaker, ShieldCheck, AlertTriangle, ChevronDown, ShoppingCart, FileText, Tag } from "lucide-react";import { useNavigate, useParams } from "react-router-dom";
import { FilterAndReportBar } from "@/components/FilterAndReportBar";
import TelegramSendButton from "@/components/TelegramSendButton";
import BoxLabelPreview from "@/components/BoxLabelPreview";
import { useAuth } from "@/lib/auth";
import { openPdfPreview } from "@/lib/pdfPreview";
import EquipmentUsagePanel from "@/components/EquipmentUsagePanel";
import EquipmentCleaningPanel from "@/components/EquipmentCleaningPanel";
import { usePagination, ListPagination } from "@/components/ListPagination";
import { doneLast } from "@/lib/listOrder";

const PRIORITY_BORDER = { urgent: "border-l-4 border-red-500", high: "border-l-4 border-pink-400", normal: "border-l-4 border-orange-400", low: "border-l-4 border-yellow-400" };
const PRIORITY_LABEL = { urgent: "Acil", high: "Yüksek", normal: "Normal", low: "Düşük" };
const PRIORITY_BADGE = { urgent: "bg-red-100 text-red-700 border-red-300", high: "bg-pink-100 text-pink-700 border-pink-300", normal: "bg-orange-100 text-orange-700 border-orange-300", low: "bg-yellow-100 text-yellow-700 border-yellow-300" };

export function ProductionOrdersPage() {
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [fillDrafts, setFillDrafts] = useState({});
  const [busy, setBusy] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editData, setEditData] = useState({});
  const [shortMap, setShortMap] = useState({});
  const [shortageModal, setShortageModal] = useState(null); // {prodNo, items:[{name, shortage, unit}]}
  const [qcModal, setQcModal] = useState(null);   // {po, specs:[{name,value,unit,min,max}], notes, loading}
  const [poModal, setPoModal] = useState(null);   // {material, prefillQty, supplier, qty, unit, price}
  const [labelFor, setLabelFor] = useState(null); // {productionOrderId, prodNo} — Etiket Yazdır
  const nav = useNavigate();
  const { user: authUser } = useAuth();
  const isAdmin = authUser?.role === "admin";
  const [fillEdits, setFillEdits] = useState({});
  const load = useCallback(async () => {
    const [p, pr, c] = await Promise.all([api.get("/production-orders"), api.get("/products"), api.get("/customers")]);
    setRows(p); setProducts(pr); setCustomers(c);
    const openPos = p.filter(x => x.status !== "completed" && x.status !== "cancelled");
    const results = await Promise.all(openPos.map(po => api.get(`/production-orders/${po.id}/shortage-check`).catch(() => null)));
    const map = {};
    openPos.forEach((po, i) => { if (results[i]) map[po.id] = results[i]; });
    setShortMap(map);
  }, []);
  useEffect(() => { load(); }, []);
  // Live sync — re-poll every 15s so as-received inventory dissolves shortages on the list in real-time.
  useEffect(() => {
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [load]);
  const pname = id => products.find(p => p.id === id)?.name || "—";
  const del = async (id) => { if (window.confirm("Üretim emri iptal edilsin mi?")) { try { await api.del(`/production-orders/${id}`); load(); } catch (e) { alert(e.response?.data?.detail); } } };
  const doFill = async (r, slot) => {
    const qty = parseInt(fillDrafts[`${r.id}_${slot}`] || 0);
    if (!qty || qty <= 0) { alert("Adet girin"); return; }
    if (!r.production_approved_at) { alert("Önce 'Üretim Onayı' verilmeli."); return; }
    if (!r.production_qc_approved_at) { alert("Önce 'QC Onayı' verilmeli."); return; }
    if (!(r.actual_bulk_kg > 0)) { alert("Önce 'Bulk' butonu ile gerçekleşen bulk (kg) girmelisiniz."); return; }
    // [msg-package-01 C] Frontend max validation — sipariş adedinin %30 fazlası.
    const soQty = Number(r.sales_order_qty || r.planned_units || 0);
    if (soQty > 0) {
      const maxTotal = Math.floor(soQty * 1.30);
      const prevTotal = (r.filling_operations || []).reduce((s, op) => s + Number(op.accepted_units || 0), 0);
      const remaining = Math.max(0, maxTotal - prevTotal);
      if (qty + prevTotal > maxTotal) {
        alert(
          `Maksimum dolum miktarı sipariş adedinin %30 fazlası olabilir.\n` +
          `Bu sipariş için maksimum toplam dolum: ${maxTotal.toLocaleString('tr-TR')} adet.\n` +
          (prevTotal > 0
            ? `Mevcut dolum: ${prevTotal.toLocaleString('tr-TR')} adet. Kalan maksimum dolum hakkı: ${remaining.toLocaleString('tr-TR')} adet.`
            : "")
        );
        return;
      }
    }
    setBusy(`${r.id}_${slot}`);
    try {
      await api.post(`/production-orders/${r.id}/complete-filling`, { accepted_units: qty });
      setFillDrafts({ ...fillDrafts, [`${r.id}_${slot}`]: "" });
      await load();
    } catch (e) { alert(e.response?.data?.detail); }
    finally { setBusy(null); }
  };
  const complete = async (r) => { if (window.confirm("Üretim tamamlansın mı? Sonraki dolum yapılamayacak.")) { try { await api.post(`/production-orders/${r.id}/complete`); load(); } catch (e) { alert(e.response?.data?.detail); } } };
  const quickBulk = async (r) => {
    const v = prompt(`Üretilecek Miktar (kg)? Planlanan: ${r.planned_batch_kg}`, r.planned_batch_kg);
    if (!v) return;
    try { await api.post(`/production-orders/${r.id}/complete-bulk`, { actual_bulk_kg: parseFloat(v) }); load(); } catch (e) { alert(e.response?.data?.detail); }
  };
  const approveProduction = async (r, force = false) => {
    // Bulk must already be entered — enforced by backend
    try {
      const qs = force ? "?force=true" : "";
      await api.post(`/production-orders/${r.id}/approve-production${qs}`);
      load();
    } catch (e) {
      const det = e.response?.data?.detail;
      if (e.response?.status === 409 && det && det.code === "material_shortage") {
        const list = det.shortages.map(s => `• ${s.material_name || "?"} (${s.kind === "raw" ? "Hammadde" : "Ambalaj"}): eksik ${s.shortage} ${s.unit || ""} (gerekli ${s.required}, mevcut ${s.available})`).join("\n");
        const proceed = window.confirm(`⚠ EKSİK STOK UYARISI\n\nAşağıdaki malzemeler yeterli değil:\n\n${list}\n\nYine de üretim onayı VERMEK istediğinize emin misiniz?`);
        if (proceed) approveProduction(r, true);
      } else {
        alert(typeof det === "string" ? det : (det?.message || "Onay verilemedi"));
      }
    }
  };
  const qcApprove = async (r) => {
    // Open modal with formula QC specs — all values must be entered
    try {
      const form = await api.get(`/production-orders/${r.id}/qc-form`);
      const specs = (form.formula_qc_specs || []).map(s => ({ ...s, value: "" }));
      if (specs.length === 0) {
        alert("Bu üretim emrinin formülünde QC parametresi tanımlı değil. Önce formüle QC parametreleri ekleyin.");
        return;
      }
      setQcModal({ po: r, specs, notes: "", retained: { collected: null, quantity: 2, unit: "adet", storage_location: "Şahit Numune Dolabı", taken_at: new Date().toISOString().slice(0,10), retention_end: "", notes: "" }, saving: false });
    } catch (e) { alert(e.response?.data?.detail || "QC formu yüklenemedi"); }
  };
  const saveQcModal = async () => {
    if (!qcModal) return;
    const missing = qcModal.specs.filter(s => (s.value ?? "") === "");
    if (missing.length > 0) { alert(`Aşağıdaki QC parametreleri boş — değer girmelisiniz:\n\n• ${missing.map(m => m.name).join("\n• ")}`); return; }
    // [iter69 fix] Şahit Numune için açık Evet/Hayır seçimi zorunlu (GMP).
    const rsCollected = qcModal.retained?.collected;
    if (rsCollected !== true && rsCollected !== false) {
      alert("Şahit Numune için Evet veya Hayır seçmelisiniz (GMP kaydı).");
      return;
    }
    setQcModal({ ...qcModal, saving: true });
    try {
      const payload = { specs: qcModal.specs, notes: qcModal.notes };
      payload.retained_sample = { ...qcModal.retained, collected: rsCollected };
      const res = await api.post(`/production-orders/${qcModal.po.id}/qc-approve`, payload);
      if (res?.retained_sample_id) {
        // toast/alert
        try { alert("QC onaylandı · Şahit numune otomatik oluşturuldu (GMP > Şahit Numune)."); } catch (_) {}
      }
      setQcModal(null); load();
    } catch (e) {
      alert(e.response?.data?.detail || "QC onaylanamadı");
      setQcModal({ ...qcModal, saving: false });
    }
  };
  const openPoDraft = (shortage) => {
    setPoModal({
      material_id: shortage.material_id,
      material_name: shortage.material_name,
      unit: shortage.unit || "kg",
      supplier: "",
      qty: shortage.shortage,
      unit_price: 0,
      expected_delivery: "",
      saving: false,
    });
  };
  const savePoDraft = async () => {
    if (!poModal) return;
    if (!poModal.supplier.trim()) { alert("Tedarikçi zorunlu"); return; }
    if (!poModal.qty || parseFloat(poModal.qty) <= 0) { alert("Miktar 0'dan büyük olmalı"); return; }
    setPoModal({ ...poModal, saving: true });
    try {
      const r = await api.post("/purchase-orders", {
        supplier: poModal.supplier.trim(),
        expected_delivery: poModal.expected_delivery || undefined,
        lines: [{ material_id: poModal.material_id, qty: parseFloat(poModal.qty), unit: poModal.unit, unit_price: parseFloat(poModal.unit_price || 0) }],
      });
      alert(`Satın alma taslağı oluşturuldu: ${r.po_no}\nOnay için Satın Alma sayfasından "Onayla" deyin.`);
      setPoModal(null);
    } catch (e) {
      alert(e.response?.data?.detail || "PO oluşturulamadı");
      setPoModal({ ...poModal, saving: false });
    }
  };
  const openEdit = (r) => { setEditing(r); setFillEdits({}); setEditData({ planned_batch_kg: r.planned_batch_kg, planned_units: r.planned_units, actual_bulk_kg: r.actual_bulk_kg || 0, priority: r.priority || "normal", notes: r.notes || "" }); };
  const saveFilling = async (idx) => {
    const v = parseInt(fillEdits[idx], 10);
    if (Number.isNaN(v) || v < 0) { alert("Geçerli adet girin"); return; }
    try {
      await api.put(`/production-orders/${editing.id}/filling/${idx}`, { accepted_units: v });
      const fresh = (await api.get("/production-orders")).find(x => x.id === editing.id);
      if (fresh) setEditing(fresh);
      setFillEdits({ ...fillEdits, [idx]: undefined });
      load();
    } catch (e) { alert(e.response?.data?.detail || "Kaydedilemedi"); }
  };
  const saveEdit = async () => {
    try { await api.put(`/production-orders/${editing.id}`, editData); setEditing(null); load(); }
    catch (e) { alert(e.response?.data?.detail); }
  };
  const visibleRows = doneLast(filteredRows.length > 0 || rows.length === 0 ? filteredRows : rows, r => !!r.final_approved_at, r => r.final_approved_at);
  const pg = usePagination(visibleRows, "production-orders", 100);
  const pagedRows = pg.paged;
  return (
    <div>
      <PageHeader title="Üretim Emirleri" description="Üretim onayı → QC onayı → 3 kademe dolum → Tamamlandı" />
      <FilterAndReportBar
        reportType="production" rows={rows} customers={customers}
        statuses={[{value:"draft",label:"Taslak"},{value:"in_progress",label:"Devam"},{value:"awaiting_qc_bulk",label:"Bulk QC"},{value:"awaiting_qc_finished",label:"Bitmiş QC"},{value:"completed",label:"Tamamlandı"},{value:"cancelled",label:"İptal"}]}
        dateAccessor={r => r.created_at}
        customerAccessor={r => r.customer_id}
        productAccessor={r => pname(r.product_id)}
        statusAccessor={r => r.status}
        onFiltered={setFilteredRows}
      />
      <div className="overflow-x-auto bg-white border border-slate-200 rounded-lg">
        <table className="w-full text-sm" data-testid="prod-table">
          <thead className="bg-slate-50/50 border-b border-slate-200">
            <tr>
            {["", "Öncelik", "Üretim No", "Proje No", "Müşteri", "Ürün", "Sipariş Adeti", "Üretilecek Miktar (kg)", "Üretim", "QC", "Dolumlar", "Toplam Adet", "Fire (kg)", "Durum", "İşlem"].map((h, i) => (
                <th key={i} className="text-left px-2 py-2 text-[11px] font-semibold text-slate-600 uppercase tracking-wide whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pagedRows.length === 0 && <tr><td colSpan={14} className="text-center py-10 text-slate-400">Kayıt yok</td></tr>}
            {pagedRows.map(r => {
              const ops = r.filling_operations || [];
              const prodApproved = !!r.production_approved_at;
              const qcApproved = !!r.production_qc_approved_at;
              const shortInfo = shortMap[r.id];
              // iter270 — QC onayından sonra üretim bitmiş sayılır: eksik malzeme
              // uyarısını görmezden gel, ✓ göster. Onaydan önce shortage-check'e uy.
              const hasShort = shortInfo?.has_shortage && !r.production_qc_approved_at;
              const shortageItems = (shortInfo?.shortages || []).map(s => ({
                name: s.material_name || "?",
                shortage: s.shortage,
                unit: s.unit || "",
              }));
              const prio = r.priority || "normal";
              // Fire (spec rules 48-52): live waste = üretilecek miktar - toplam dolum bulk kullanımı.
              const uretilecekKg = parseFloat(r.actual_bulk_kg || 0);
              const filledBulk = ops.reduce((s, op) => s + parseFloat(op.bulk_consumed_kg || op.bulk_used_kg || op.bulk_used || 0), 0);
              const fireKg = uretilecekKg > 0 ? Math.max(0, uretilecekKg - filledBulk) : (r.remaining_bulk_kg || 0);
              const nextSlot = ops.length;  // dynamic: only next unused slot is editable
              // [iter71] Dolum girişi "Üretimi Tamamla" butonuna basılana kadar açık kalır.
              // Bulk tükendiğinde veya sipariş adetine ulaşıldığında uyarı verilmez —
              // kullanıcı istediği kadar dolum ekleyebilir (bkz. msg-iter71 kural).
              const canAddFill = prodApproved && qcApproved && uretilecekKg > 0 && r.status !== "completed" && r.status !== "cancelled";
              return (
                <React.Fragment key={r.id}>
                <tr className={`border-b border-slate-100 hover:bg-slate-50 ${PRIORITY_BORDER[prio] || ""}`}>
                  <td className="px-1 py-1.5 w-6">
                    {shortInfo == null ? (
                      <span className="text-slate-300 text-xs" title="Envanter kontrol bilgisi yükleniyor…">…</span>
                    ) : hasShort ? (
                      <div className="relative group inline-block">
                        <button
                          type="button"
                          onClick={() => setShortageModal({ prodNo: r.prod_no, items: shortageItems })}
                          data-testid={`shortage-info-${r.id}`}
                          className="flex items-center text-amber-600 hover:text-amber-700"
                          aria-label="Eksik malzeme bilgisi"
                        >
                          <AlertTriangle size={16} className="animate-pulse" />
                        </button>
                        {/* Hover tooltip — compact eksik özet (yalnız bilgi) */}
                        <div className="pointer-events-none absolute z-30 left-6 top-0 hidden group-hover:block bg-slate-900 text-white text-[11px] rounded-md shadow-lg px-3 py-2 min-w-[200px] max-w-[280px]">
                          <div className="font-semibold text-amber-300 mb-1 flex items-center gap-1">
                            <AlertTriangle size={11}/> Eksik Malzeme ({shortageItems.length})
                          </div>
                          <ul className="space-y-0.5">
                            {shortageItems.slice(0, 5).map((it, i) => (
                              <li key={i} className="flex justify-between gap-2">
                                <span className="truncate">{it.name}</span>
                                <span className="font-mono text-amber-200 whitespace-nowrap">{it.shortage} {it.unit}</span>
                              </li>
                            ))}
                            {shortageItems.length > 5 && (
                              <li className="italic text-slate-300 pt-0.5">+{shortageItems.length - 5} daha… (detay için tıklayın)</li>
                            )}
                          </ul>
                          <div className="mt-1 text-[10px] italic text-slate-400">Bu ekran yalnızca bilgi amaçlıdır.</div>
                        </div>
                      </div>
                    ) : (
                      <span className="text-emerald-500 text-base" title="Malzeme yeterli — envanter kontrolünde eksik yok" data-testid={`shortage-ok-${r.id}`}>✓</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${PRIORITY_BADGE[prio] || PRIORITY_BADGE.normal}`}>{PRIORITY_LABEL[prio] || prio}</span>
                  </td>
                  <td className="px-2 py-1.5"><span className="font-mono text-xs cursor-pointer text-indigo-600" onClick={() => nav(`/production/${r.id}`)} data-testid={`prod-no-${r.id}`}>{r.prod_no}</span></td>
                  <td className="px-2 py-1.5 font-mono text-xs text-slate-700" data-testid={`prod-project-${r.id}`}>{r.project_no || <span className="text-slate-300">—</span>}</td>
                  <td className="px-2 py-1.5 text-xs" data-testid={`prod-customer-${r.id}`}>
                    {(() => {
                      const c = customers.find(x => x.id === r.customer_id);
                      return c ? (c.legal_name || c.name || c.company_name || "—") : <span className="text-slate-300">—</span>;
                    })()}
                  </td>
                  <td className="px-2 py-1.5 text-xs">{r.product_name || pname(r.product_id)}</td>
                  <td className="px-2 py-1.5 font-mono text-xs" data-testid={`prod-order-qty-${r.id}`}>{r.planned_units}</td>
                  <td className="px-2 py-1.5">
                    {uretilecekKg > 0 ? <span className="font-mono text-xs" data-testid={`prod-uretilecek-${r.id}`}>{uretilecekKg}</span> : r.status !== "completed" && r.status !== "cancelled" ? <Btn size="sm" variant="secondary" onClick={() => quickBulk(r)} data-testid={`quick-bulk-${r.id}`}>Miktar Gir</Btn> : "—"}
                  </td>
                  <td className="px-2 py-1.5">
                    {prodApproved ? <Badge level="ok">✓</Badge> :
                      (r.status !== "completed" && r.status !== "cancelled") ?
                        <Btn size="lg" variant="approve" onClick={() => approveProduction(r)} data-testid={`approve-prod-${r.id}`}>Üretim Onayı</Btn> : "—"}
                  </td>
                  <td className="px-2 py-1.5">
                    {qcApproved ? <Badge level="ok">✓</Badge> :
                      prodApproved && r.status !== "completed" && r.status !== "cancelled" ?
                        <Btn size="lg" variant="approve" onClick={() => qcApprove(r)} data-testid={`qc-approve-${r.id}`}><ShieldCheck size={12}/> QC Onayı</Btn> : "—"}
                  </td>
                  <td className="px-2 py-1.5">
                    {/* Dynamic dolum column — spec rules 36-40: list existing fills + a single "next" input if bulk remains */}
                    <div className="flex flex-col gap-1">
                      {ops.map((op, k) => (
                        <div key={k} className="text-xs flex items-center gap-1" data-testid={`prod-fill-${r.id}-${k}`}>
                          <span className="text-[10px] font-semibold text-slate-500">{k + 1}.</span>
                          <span className="font-mono font-semibold">{op.accepted_units}</span>
                          <span className="text-[10px] text-slate-400 font-mono">adet</span>
                        </div>
                      ))}
                      {canAddFill && (
                        <div className="flex gap-1" data-testid={`prod-fill-add-${r.id}`}>
                          <input type="number" placeholder={`${nextSlot + 1}. dolum adet`} value={fillDrafts[`${r.id}_${nextSlot}`] || ""} onChange={e => setFillDrafts({ ...fillDrafts, [`${r.id}_${nextSlot}`]: e.target.value })} className="w-24 px-1 py-0.5 text-xs border rounded" data-testid={`fill-in-${r.id}-${nextSlot}`}/>
                          <button onClick={() => doFill(r, nextSlot)} disabled={busy === `${r.id}_${nextSlot}`} className="text-xs bg-indigo-600 text-white px-1.5 py-0.5 rounded" data-testid={`fill-btn-${r.id}-${nextSlot}`}>+</button>
                        </div>
                      )}
                      {ops.length === 0 && !canAddFill && <span className="text-slate-300 text-xs">—</span>}
                    </div>
                  </td>
                  <td className="px-2 py-1.5 font-mono font-semibold text-xs" data-testid={`prod-total-${r.id}`}>{r.total_filled_units || 0}</td>
                  <td className="px-2 py-1.5 font-mono text-xs" data-testid={`prod-fire-${r.id}`}>{fireKg > 0 ? <span className={r.status === "completed" ? "text-red-600 font-semibold" : "text-amber-700"}>{fireKg.toFixed(2)}</span> : <span className="text-slate-300">0</span>}</td>
                  <td className="px-2 py-1.5"><Badge level={r.status === "completed" ? "ok" : r.status === "blocked" || r.status === "cancelled" ? "missing" : "attention"}>{PROD_STATUS_LABEL[r.status] || r.status}</Badge></td>
                  <td className="px-2 py-1.5">
                    <div className="flex gap-1 flex-wrap">
                      {((r.status !== "completed" && r.status !== "cancelled") || (r.status === "completed" && isAdmin && !r.final_approved_at)) && <Btn size="sm" variant="secondary" onClick={() => openEdit(r)} data-testid={`edit-prod-${r.id}`}>Düzenle</Btn>}
                      {r.status !== "completed" && r.status !== "cancelled" && <Btn size="sm" onClick={() => complete(r)} data-testid={`complete-${r.id}`}><CheckCircle size={12}/> Tamamla</Btn>}
                      {r.status === "completed" && (
                        <Btn size="sm" variant="secondary" onClick={() => setLabelFor({ productionOrderId: r.id, prodNo: r.prod_no })} data-testid={`label-prod-${r.id}`} title="100×100 mm koli etiketi">
                          <Tag size={12}/> Etiket Yazdır
                        </Btn>
                      )}
                      {r.status === "completed" && r.production_approved_at && r.production_qc_approved_at && !r.final_approved_at && (
                        <Btn size="sm" variant="approve" onClick={async () => {
                          if (!window.confirm(`${r.prod_no} için SON ONAY yapılacak.\n\nBu işlem sadece Production Order dokümanının nihai idari onayıdır — stok/teslimat/İrsaliye ETKİLENMEZ. Devam?`)) return;
                          try { await api.post(`/production-orders/${r.id}/final-approve`, {}); await load(); }
                          catch (e) { alert(e?.response?.data?.detail || "Son onay yapılamadı"); }
                        }} data-testid={`final-approve-${r.id}`} title="Son Onay — üretim dokümanı final approval">
                          <ShieldCheck size={12}/> Son Onay
                        </Btn>
                      )}
                      {r.final_approved_at && (
                        <span className="text-[10px] text-emerald-700 font-semibold px-2 py-1 rounded bg-emerald-50 border border-emerald-200" title={`${r.final_approved_by_name || "?"} · ${r.final_approved_at}`} data-testid={`final-approved-badge-${r.id}`}>
                          ✓ Son Onaylandı
                        </span>
                      )}
                      {r.status !== "completed" && <Btn size="sm" variant="danger" onClick={() => del(r.id)} data-testid={`delete-prod-${r.id}`}>Sil</Btn>}
                    </div>
                  </td>
                </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <ListPagination pagination={pg} testid="production-pagination"/>

      <Modal open={!!editing} onClose={() => setEditing(null)} title={`Üretim Emri Düzenle — ${editing?.prod_no || ""}`}>
        {editing?.status !== "completed" && (<>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input label="Sipariş Adeti" type="number" value={editData.planned_units || ""} onChange={e => setEditData({ ...editData, planned_units: parseInt(e.target.value) })} data-testid="edit-units"/>
          <Input label="Üretilecek Miktar (kg)" type="number" step="0.01" value={editData.actual_bulk_kg || ""} onChange={e => setEditData({ ...editData, actual_bulk_kg: parseFloat(e.target.value) })} data-testid="edit-uretilecek"/>
          <Select label="Öncelik" value={editData.priority || "normal"} onChange={e => setEditData({ ...editData, priority: e.target.value })} data-testid="edit-priority">
            <option value="low">Düşük</option><option value="normal">Normal</option><option value="high">Yüksek</option><option value="urgent">Acil</option>
          </Select>
        </div>
        <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-[11px] text-amber-800">
          <b>Not:</b> Üretilecek Miktar değişikliği; üretim henüz onaylanmadıysa formül hammadde miktarlarını, FIFO lot dağılımını ve rezervasyonu otomatik yeniden hesaplar. Onay verildikten sonra sadece admin değiştirebilir; geçmiş lot tüketimi bozulmaz.
        </div>
        <Textarea label="Notlar" value={editData.notes || ""} onChange={e => setEditData({ ...editData, notes: e.target.value })} />
        </>)}
        {isAdmin && !editing?.final_approved_at && (editing?.filling_operations || []).length > 0 && (
          <div className="mt-3 border rounded p-2" data-testid="edit-filling-section">
            <div className="text-xs font-semibold text-slate-700 mb-1">Dolum Adetleri (Son Onaya kadar düzeltilebilir)</div>
            <div className="text-[11px] text-slate-500 mb-2">Yalnız dolum adedi ve bitmiş ürün adedi değişir; hammadde tüketimi, formül, üretim miktarı ve onaylar değişmez.</div>
            {(editing.filling_operations || []).map((op, idx) => (
              <div key={idx} className="flex items-center gap-2 mb-1 text-xs">
                <span className="w-20">{idx + 1}. dolum</span>
                <span className="font-mono w-16 text-slate-500">{op.accepted_units}</span>
                <input type="number" min="0" className="w-24 px-1 py-0.5 border rounded" placeholder="Yeni adet"
                  value={fillEdits[idx] ?? ""} onChange={e => setFillEdits({ ...fillEdits, [idx]: e.target.value })} data-testid={`edit-fill-input-${idx}`}/>
                <Btn size="sm" onClick={() => saveFilling(idx)} disabled={fillEdits[idx] === undefined || fillEdits[idx] === ""} data-testid={`edit-fill-save-${idx}`}>Kaydet</Btn>
              </div>
            ))}
          </div>
        )}
        {editing?.status === "completed" && !(editing?.filling_operations || []).length && (
          <div className="text-xs text-slate-500 italic p-2" data-testid="edit-filling-empty">Bu üretim emrinde düzeltilecek dolum kaydı yok.</div>
        )}
        <div className="flex justify-end gap-2 mt-4"><Btn variant="secondary" onClick={() => setEditing(null)}>{editing?.status === "completed" ? "Kapat" : "Vazgeç"}</Btn>{editing?.status !== "completed" && <Btn onClick={saveEdit} data-testid="save-edit-prod">Kaydet</Btn>}</div>
      </Modal>

      <Modal open={!!qcModal} onClose={() => !qcModal?.saving && setQcModal(null)} title={`QC Onayı — ${qcModal?.po?.prod_no || ""}`}>
        <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 mb-3 text-amber-800">
          <ShieldCheck size={12} className="inline mr-1"/> Tüm QC parametrelerinin değerlerini eksiksiz doldurmadan onay verilemez. Onay verildiğinde bu üretim emri için bir QC kaydı oluşturulur.
        </div>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {(qcModal?.specs || []).map((s, i) => {
            // Auto-evaluation: measured value within [lower, upper] → Uygun (pass), outside → Uygun Değil (fail).
            const numVal = parseFloat(String(s.value ?? "").replace(",", "."));
            const hasLimits = s.lower != null && s.upper != null;
            const hasVal = s.value !== "" && s.value !== null && s.value !== undefined && !Number.isNaN(numVal);
            const inRange = hasLimits && hasVal ? (numVal >= parseFloat(s.lower) && numVal <= parseFloat(s.upper)) : null;
            return (
            <div key={i} className={`grid grid-cols-12 gap-2 items-end rounded p-2 border ${inRange === false ? "bg-red-50 border-red-300" : inRange === true ? "bg-emerald-50 border-emerald-300" : "bg-slate-50 border-slate-200"}`}>
              <div className="col-span-4 text-xs">
                <div className="font-semibold text-slate-700">{s.name}</div>
                <div className="text-slate-400 text-[10px]">{s.lower != null ? `Alt: ${s.lower}` : ""} {s.upper != null ? `Üst: ${s.upper}` : ""} {s.unit ? `(${s.unit})` : ""}</div>
              </div>
              <div className="col-span-5">
                <input type="text" placeholder="Değer" value={s.value ?? ""} onChange={e => { const sp = [...qcModal.specs]; sp[i] = { ...sp[i], value: e.target.value }; setQcModal({ ...qcModal, specs: sp }); }} className="w-full px-2 py-1 text-xs border rounded" data-testid={`qc-modal-value-${i}`}/>
              </div>
              <div className="col-span-1 text-[10px] text-slate-500">{s.unit || ""}</div>
              <div className="col-span-2 text-center">
                {inRange === true && <span className="text-[10px] font-semibold px-2 py-1 bg-emerald-500 text-white rounded" data-testid={`qc-modal-eval-${i}`}>Uygun ✓</span>}
                {inRange === false && <span className="text-[10px] font-semibold px-2 py-1 bg-red-500 text-white rounded" data-testid={`qc-modal-eval-${i}`}>Uygun Değil ✗</span>}
                {inRange === null && <span className="text-[10px] text-slate-400">—</span>}
              </div>
            </div>
          );})}
        </div>
        {(() => {
          const specs = qcModal?.specs || [];
          const numericWithLimits = specs.filter(s => s.lower != null && s.upper != null);
          if (numericWithLimits.length === 0) return null;
          const evaluated = numericWithLimits.filter(s => {
            const v = parseFloat(String(s.value ?? "").replace(",", "."));
            return !Number.isNaN(v);
          });
          if (evaluated.length === 0) return null;
          const failing = evaluated.filter(s => {
            const v = parseFloat(String(s.value ?? "").replace(",", "."));
            return v < parseFloat(s.lower) || v > parseFloat(s.upper);
          });
          const allPass = failing.length === 0 && evaluated.length === numericWithLimits.length;
          return (
            <div className={`mt-3 p-2 rounded border text-xs font-semibold ${allPass ? "bg-emerald-50 border-emerald-300 text-emerald-800" : failing.length > 0 ? "bg-red-50 border-red-300 text-red-800" : "bg-amber-50 border-amber-300 text-amber-800"}`} data-testid="qc-modal-summary">
              Genel Sonuç: {allPass ? "Uygun ✓ (tüm ölçümler alt/üst limit içinde)" : failing.length > 0 ? `Uygun Değil ✗ (${failing.length} parametre limit dışında)` : `${evaluated.length}/${numericWithLimits.length} ölçüm tamamlandı`}
            </div>
          );
        })()}
        <Textarea label="Notlar (opsiyonel)" value={qcModal?.notes || ""} onChange={e => setQcModal({ ...qcModal, notes: e.target.value })} />

        {/* Şahit Numune (GMP) — açık Evet/Hayır seçimi zorunlu */}
        <div className="mt-3 rounded border border-purple-200 bg-purple-50/50 p-3" data-testid="qc-retained-block">
          <div className="text-xs font-semibold text-purple-900 mb-2">
            Şahit Numune Ayrıldı mı? <span className="text-red-600">*</span>
            <span className="text-[10px] font-normal text-slate-500 ml-1">(GMP zorunlu · Evet seçilirse otomatik kayıt oluşur)</span>
          </div>
          <div className="flex items-center gap-4 text-xs" data-testid="qc-retained-choice">
            <label className="flex items-center gap-1 cursor-pointer">
              <input
                type="radio"
                name="qc-retained-collected"
                checked={qcModal?.retained?.collected === true}
                onChange={() => setQcModal({ ...qcModal, retained: { ...(qcModal?.retained || {}), collected: true } })}
                data-testid="qc-retained-yes"
              />
              <span className="font-semibold text-emerald-700">Evet</span>
            </label>
            <label className="flex items-center gap-1 cursor-pointer">
              <input
                type="radio"
                name="qc-retained-collected"
                checked={qcModal?.retained?.collected === false}
                onChange={() => setQcModal({ ...qcModal, retained: { ...(qcModal?.retained || {}), collected: false } })}
                data-testid="qc-retained-no"
              />
              <span className="font-semibold text-slate-700">Hayır</span>
            </label>
            {qcModal?.retained?.collected === null && (
              <span className="text-[11px] text-red-600 italic">Seçim yapmadan QC onayı verilemez.</span>
            )}
          </div>
          {qcModal?.retained?.collected === true && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2 text-xs">
              <label className="flex flex-col"><span className="text-slate-600">Miktar</span>
                <input type="number" step="0.01" value={qcModal.retained.quantity} onChange={e => setQcModal({ ...qcModal, retained: { ...qcModal.retained, quantity: parseFloat(e.target.value) || 0 } })} className="px-2 py-1 border rounded" data-testid="qc-retained-qty"/>
              </label>
              <label className="flex flex-col"><span className="text-slate-600">Birim</span>
                <select value={qcModal.retained.unit} onChange={e => setQcModal({ ...qcModal, retained: { ...qcModal.retained, unit: e.target.value } })} className="px-2 py-1 border rounded" data-testid="qc-retained-unit">
                  <option value="adet">adet</option><option value="ml">ml</option><option value="g">g</option>
                </select>
              </label>
              <label className="flex flex-col"><span className="text-slate-600">Saklama Yeri</span>
                <input value={qcModal.retained.storage_location} onChange={e => setQcModal({ ...qcModal, retained: { ...qcModal.retained, storage_location: e.target.value } })} className="px-2 py-1 border rounded" data-testid="qc-retained-loc"/>
              </label>
              <label className="flex flex-col"><span className="text-slate-600">Ayrılma Tarihi</span>
                <input type="date" value={qcModal.retained.taken_at} onChange={e => setQcModal({ ...qcModal, retained: { ...qcModal.retained, taken_at: e.target.value } })} className="px-2 py-1 border rounded" data-testid="qc-retained-taken"/>
              </label>
              <label className="flex flex-col"><span className="text-slate-600">Saklama Bitiş (boş=12 ay)</span>
                <input type="date" value={qcModal.retained.retention_end || ""} onChange={e => setQcModal({ ...qcModal, retained: { ...qcModal.retained, retention_end: e.target.value } })} className="px-2 py-1 border rounded" data-testid="qc-retained-end"/>
              </label>
              <label className="flex flex-col col-span-3"><span className="text-slate-600">Not</span>
                <input value={qcModal.retained.notes || ""} onChange={e => setQcModal({ ...qcModal, retained: { ...qcModal.retained, notes: e.target.value } })} className="px-2 py-1 border rounded" data-testid="qc-retained-notes"/>
              </label>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Btn variant="secondary" onClick={() => setQcModal(null)} disabled={qcModal?.saving}>Vazgeç</Btn>
          <Btn variant="approve" onClick={saveQcModal} disabled={qcModal?.saving} data-testid="qc-modal-save"><ShieldCheck size={14}/> {qcModal?.saving ? "Kaydediliyor..." : "QC Onayı Ver"}</Btn>
        </div>
      </Modal>

      <Modal open={!!poModal} onClose={() => !poModal?.saving && setPoModal(null)} title={`Satın Alma Taslağı — ${poModal?.material_name || ""}`}>
        <div className="text-xs bg-indigo-50 border border-indigo-200 rounded p-2 mb-3 text-indigo-800">
          <ShoppingCart size={12} className="inline mr-1"/> Bu form onaylandığında yeni bir Satın Alma Emri (PO) taslak olarak oluşturulur. Satın Alma sayfasından "Onayla" tuşuyla tedarikçiye gönderilebilir.
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input label="Tedarikçi *" value={poModal?.supplier || ""} onChange={e => setPoModal({ ...poModal, supplier: e.target.value })} data-testid="po-draft-supplier"/>
          <Input label="Beklenen Teslim" type="date" value={poModal?.expected_delivery || ""} onChange={e => setPoModal({ ...poModal, expected_delivery: e.target.value })} data-testid="po-draft-delivery"/>
          <Input label={`Miktar (${poModal?.unit || ""})`} type="number" step="0.01" value={poModal?.qty || ""} onChange={e => setPoModal({ ...poModal, qty: e.target.value })} data-testid="po-draft-qty"/>
          <Select label="Birim" value={poModal?.unit || "kg"} onChange={e => setPoModal({ ...poModal, unit: e.target.value })}>
            <option value="kg">kg</option><option value="g">g</option><option value="l">l</option><option value="ml">ml</option>
            <option value="adet">adet</option><option value="box">kutu</option><option value="carton">koli</option><option value="roll">rulo</option>
          </Select>
          <Input label="Birim Fiyat" type="number" step="0.01" value={poModal?.unit_price || ""} onChange={e => setPoModal({ ...poModal, unit_price: e.target.value })} data-testid="po-draft-price"/>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Btn variant="secondary" onClick={() => setPoModal(null)} disabled={poModal?.saving}>Vazgeç</Btn>
          <Btn onClick={savePoDraft} disabled={poModal?.saving} data-testid="po-draft-save"><ShoppingCart size={14}/> {poModal?.saving ? "Oluşturuluyor..." : "Sipariş Oluştur"}</Btn>
        </div>
      </Modal>

      {/* Merkezi Koli Etiketi Önizleme — Üretim Emirleri kaynağı */}
      <BoxLabelPreview
        open={!!labelFor}
        onClose={() => setLabelFor(null)}
        source="production"
        productionOrderId={labelFor?.productionOrderId}
        prodNo={labelFor?.prodNo}
      />

      {/* Eksik Malzeme Bilgi Modali — YALNIZCA BİLGİ AMAÇLIDIR, hiçbir kısıtlama uygulamaz */}
      <Modal
        open={!!shortageModal}
        onClose={() => setShortageModal(null)}
        title={`Eksik Malzemeler — ${shortageModal?.prodNo || ""}`}
      >
        <div className="text-[11px] bg-amber-50 border border-amber-200 rounded p-2 mb-3 text-amber-800 flex items-start gap-2" data-testid="shortage-modal-info">
          <AlertTriangle size={14} className="mt-0.5 flex-shrink-0"/>
          <div>
            Bu ekran <b>yalnızca bilgi amaçlıdır</b>. Envanter kontrolü satış siparişleri üzerinden yapılır; burada sadece ilgili üretim emrinin eksik hammadde/ambalaj özeti gösterilir. Hiçbir işlemi engellemez.
          </div>
        </div>
        {(shortageModal?.items || []).length === 0 ? (
          <div className="text-xs italic text-slate-400 py-4 text-center">Eksik yok.</div>
        ) : (
          <div className="max-h-96 overflow-y-auto border border-slate-200 rounded" data-testid="shortage-modal-list">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600">Malzeme Adı</th>
                  <th className="text-right px-3 py-2 font-semibold text-slate-600 whitespace-nowrap">Eksik Miktar</th>
                </tr>
              </thead>
              <tbody>
                {shortageModal.items.map((it, i) => (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`shortage-modal-row-${i}`}>
                    <td className="px-3 py-2 font-medium text-slate-800">{it.name}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-amber-700 whitespace-nowrap">{it.shortage} {it.unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex justify-end mt-4">
          <Btn variant="secondary" onClick={() => setShortageModal(null)} data-testid="shortage-modal-close">Kapat</Btn>
        </div>
      </Modal>
    </div>
  );
}

export function ProductionDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  // [msg-145 Bölüm 3+4] Maliyet Anlık Görüntüsü yalnızca Sistem Yöneticisi'ne görünür.
  const isAdmin = user?.role === "admin";
  const [po, setPo] = useState(null);
  const [lots, setLots] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [fillOpen, setFillOpen] = useState(false);
  // iter237 (F01 fix) — filling submit guard + stable event id. Previously
  // these setters were called (lines 544/548/553/557/563/1000) without a
  // matching useState declaration, causing a ReferenceError white-screen
  // when Kaydet was pressed in the Filling modal. Restoring the state
  // contract; behaviour unchanged.
  const [fillSubmitting, setFillSubmitting] = useState(false);
  const [fillEventId, setFillEventId] = useState(null);
  const [bulkData, setBulkData] = useState({ actual_bulk_kg: 0, sample_kg: 0, waste_kg: 0 });
  const [fillData, setFillData] = useState({ accepted_units: 0, qc_samples: 0, defective_units: 0, packaging_waste: 0, remaining_bulk_kg: 0 });
  // iter268 — Shortage indicator shown next to title from the moment PO is created
  // (before Üretim Onayı). Reuses the same shortage-check endpoint the list page uses
  // so the UX stays consistent. Click ⚠️ opens the same details modal.
  const [detailShortInfo, setDetailShortInfo] = useState(null);
  const [detailShortageModal, setDetailShortageModal] = useState(null);
  // Iter 202 §5–7 — Traceability modal (Tartan / Kontrol Eden / Filling operator / timestamps).
  // Uses existing PATCH /api/production/{id}/traceability (Iter 201 endpoint).
  // §9: single weighing metric only (Tartılacak Miktar) — no second "actual" quantity captured here.
  const [traceOpen, setTraceOpen] = useState(false);
  const [traceDraft, setTraceDraft] = useState({ weighing: [], filling: [], packaging: [] });
  // Iter244 (Faz 3C) — Display-only unit selector. Canonical DB stays in kg.
  const [prodUnit, setProdUnit] = useState(() => {
    try { return localStorage.getItem("prod_display_unit") || "kg"; } catch { return "kg"; }
  });
  const fmtMass = (kg) => {
    if (kg === null || kg === undefined || kg === "" || kg === "—") return "—";
    const v = Number(kg);
    if (!isFinite(v)) return "—";
    return prodUnit === "g" ? `${(v * 1000).toFixed(2)} g` : `${v.toFixed(2)} kg`;
  };

  const load = () => Promise.all([api.get(`/production-orders/${id}`), api.get("/lots", { status: "approved" }), api.get("/materials")])
    .then(([p, l, m]) => { setPo(p); setLots(l); setMaterials(m); });
  useEffect(() => { load(); }, [id]);
  // iter268 — Fetch shortage info alongside PO detail so the ✓/⚠️ badge is
  // accurate from the moment the PO is created (even in draft, before Üretim
  // Onayı). Re-poll every 15s to mirror the list page's behaviour.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    const fetchShortage = async () => {
      try {
        const r = await api.get(`/production-orders/${id}/shortage-check`);
        if (!cancelled) setDetailShortInfo(r);
      } catch {
        if (!cancelled) setDetailShortInfo(null);
      }
    };
    fetchShortage();
    const iv = setInterval(fetchShortage, 15000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [id]);
  if (!po) return <div className="p-6">Yükleniyor...</div>;

  const weigh = async (i, lot_id, actual_qty) => {
    try { await api.post(`/production-orders/${id}/weigh`, { row_index: i, lot_id, actual_qty: parseFloat(actual_qty) }); load(); }
    catch (e) { alert(e.response?.data?.detail || "Hata"); }
  };
  const completeBulk = async () => { await api.post(`/production-orders/${id}/complete-bulk`, bulkData); setBulkOpen(false); load(); };
  const completeFilling = async () => {
    if (fillSubmitting) return;  // iter222 §7 — double-click guard
    // [msg-package-01 C] Frontend max validation — sipariş adedinin %30 fazlası.
    const soQty = Number(po?.sales_order_qty || po?.planned_units || 0);
    if (soQty > 0) {
      const maxTotal = Math.floor(soQty * 1.30);
      const prevTotal = (po?.filling_operations || []).reduce((s, op) => s + Number(op.accepted_units || 0), 0);
      const newQty = Number(fillData?.accepted_units || 0);
      const remaining = Math.max(0, maxTotal - prevTotal);
      if (newQty + prevTotal > maxTotal) {
        alert(
          `Maksimum dolum miktarı sipariş adedinin %30 fazlası olabilir.\n` +
          `Bu sipariş için maksimum toplam dolum: ${maxTotal.toLocaleString('tr-TR')} adet.\n` +
          (prevTotal > 0
            ? `Mevcut dolum: ${prevTotal.toLocaleString('tr-TR')} adet. Kalan maksimum dolum hakkı: ${remaining.toLocaleString('tr-TR')} adet.`
            : "")
        );
        return;
      }
    }
    setFillSubmitting(true);
    try {
      // iter222 §6 — generate a stable event id ONCE per press; reuse across
      // network retries. Only reset when the modal is re-opened for a new event.
      let eid = fillEventId;
      if (!eid) {
        eid = (typeof crypto !== "undefined" && crypto.randomUUID)
          ? crypto.randomUUID()
          : `fill-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        setFillEventId(eid);
      }
      const r = await api.post(`/production-orders/${id}/complete-filling`, { ...fillData, client_event_id: eid });
      setFillOpen(false);
      setFillEventId(null);      // done — next open uses a fresh event id
      load();
      alert(`Bitmiş ürün lot: ${r.finished_lot?.lot_code || r.operation?.finished_lot_code || "?"}`);
    } catch (e) {
      alert(e.response?.data?.detail || "Dolum kaydedilemedi");
    } finally {
      setFillSubmitting(false);
    }
  };
  const mname = id => materials.find(m => m.id === id)?.name || "—";

  // Iter 202 §5 — Open traceability modal, seeded from existing PO records.
  const openTraceability = () => {
    const now = new Date().toISOString();
    setTraceDraft({
      weighing: (po.weighing || []).map((w, idx) => ({
        index: idx,
        material_name: mname(w.material_id),
        phase: w.phase,
        weighed_by_name: w.weighed_by_name || "",
        weighed_at: w.weighed_at || "",
        checked_by_name: w.checked_by_name || "",
        checked_at: w.checked_at || "",
      })),
      filling: (po.filling_operations || []).map((f, idx) => ({
        index: idx,
        operator_name: f.operator_name || "",
        started_at: f.started_at || "",
        completed_at: f.completed_at || (f.filled_at || ""),
      })),
      packaging: (po.materials_consumed || []).map((p, idx) => ({
        index: idx,
        material_name: mname(p.material_id),
        operator_name: p.operator_name || "",
        consumed_at: p.consumed_at || "",
      })),
      now,
    });
    setTraceOpen(true);
  };

  const assignMeToWeighing = (idx, field) => {
    const draft = { ...traceDraft };
    const now = new Date().toISOString();
    draft.weighing[idx] = {
      ...draft.weighing[idx],
      [`${field}_name`]: user?.name || user?.username || "unknown",
      [`${field}_at`]: draft.weighing[idx][`${field}_at`] || now,
    };
    setTraceDraft(draft);
  };
  const assignMeToFilling = (idx, field) => {
    const draft = { ...traceDraft };
    const now = new Date().toISOString();
    if (field === "operator") {
      draft.filling[idx] = { ...draft.filling[idx], operator_name: user?.name || user?.username || "unknown" };
    } else {
      draft.filling[idx] = { ...draft.filling[idx], [field]: draft.filling[idx][field] || now };
    }
    setTraceDraft(draft);
  };
  const saveTraceability = async () => {
    // Only send rows that have some data — avoid touching untouched rows.
    const payload = {
      weighing: traceDraft.weighing
        .filter(w => w.weighed_by_name || w.checked_by_name || w.weighed_at || w.checked_at)
        .map(w => ({ index: w.index,
                      weighed_by_id: user?.id, weighed_by_name: w.weighed_by_name || undefined,
                      weighed_at: w.weighed_at || undefined,
                      checked_by_id: user?.id, checked_by_name: w.checked_by_name || undefined,
                      checked_at: w.checked_at || undefined })),
      filling: traceDraft.filling
        .filter(f => f.operator_name || f.started_at || f.completed_at)
        .map(f => ({ index: f.index,
                      operator_id: user?.id, operator_name: f.operator_name || undefined,
                      started_at: f.started_at || undefined,
                      completed_at: f.completed_at || undefined })),
      packaging: traceDraft.packaging
        .filter(p => p.operator_name || p.consumed_at)
        .map(p => ({ index: p.index,
                      operator_id: user?.id, operator_name: p.operator_name || undefined,
                      consumed_at: p.consumed_at || undefined })),
    };
    try {
      const r = await api.patch(`/production/${id}/traceability`, payload);
      alert(`İzlenebilirlik kaydedildi (${r.changed || 0} değişiklik).`);
      setTraceOpen(false); load();
    } catch (e) { alert(e.response?.data?.detail || "Kaydedilemedi"); }
  };

  return (
    <div className="flex flex-col">
      <PageHeader title={`Üretim Emri ${po.prod_no}`} description={
        <div className="flex items-center gap-3 flex-wrap">
          <span>{PROD_STATUS_LABEL[po.status]}</span>
          {/* iter268 — Shortage indicator (informational). Visible from creation
              onwards; NOT gated by production approval. Click to view details.
              iter270 — QC onayından sonra üretim bitmiş sayılır; eksik malzeme
              takibi kapanır ve ✓ gösterilir. */}
          {detailShortInfo == null ? (
            <span className="text-slate-300 text-xs" title="Envanter kontrol bilgisi yükleniyor…">…</span>
          ) : (detailShortInfo.has_shortage && !po.production_qc_approved_at) ? (
            <button
              type="button"
              onClick={() => setDetailShortageModal({
                prodNo: po.prod_no,
                items: (detailShortInfo.shortages || []).map(s => ({
                  name: s.material_name || "?",
                  shortage: s.shortage,
                  unit: s.unit || "",
                })),
              })}
              data-testid={`detail-shortage-info-${po.id}`}
              className="inline-flex items-center gap-1 text-amber-700 hover:text-amber-800 bg-amber-50 border border-amber-300 rounded-full px-2 py-0.5"
              title="Eksik malzeme — Detay için tıklayın"
              aria-label="Eksik malzeme bilgisi"
            >
              <AlertTriangle size={14} className="animate-pulse" />
              <span className="text-xs font-semibold">Eksik Malzeme ({(detailShortInfo.shortages || []).length})</span>
            </button>
          ) : (
            <span
              className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-300 rounded-full px-2 py-0.5"
              title="Malzeme yeterli — envanter kontrolünde eksik yok"
              data-testid={`detail-shortage-ok-${po.id}`}
            >
              <CheckCircle size={14} />
              <span className="text-xs font-semibold">Malzeme Tam</span>
            </span>
          )}
        </div>
      }
        actions={
          <>
            <Btn variant="secondary" onClick={() => api.download(`/production-orders/${id}/pdf?unit=${prodUnit}`, `UretimEmri-${po.prod_no}.pdf`)} data-testid="print-po">PDF</Btn>
            {/* Iter 201 — Dokümanlar dropdown (compact). Reuses existing PdfPreviewDialog via openPdfPreview.
                NOTE: NO 'Hammadde Tartım Formu' item — covered by BMR (§11, §31). */}
            <details className="relative" data-testid="production-documents-menu">
              <summary className="cursor-pointer list-none inline-flex items-center gap-1 px-3 py-1.5 rounded border text-sm bg-white hover:bg-slate-50">
                Dokümanlar <ChevronDown size={12}/>
              </summary>
              <div className="absolute right-0 mt-1 w-64 bg-white border rounded-md shadow-lg z-20">
                {[
                  ["bulk-tracking", "Bulk Üretim Takip", "Bulk_Takip"],
                  ["filling", "Dolum Takip", "Dolum_Takip"],
                  ["packaging", "Ambalajlama Takip", "Ambalajlama_Takip"],
                  ["waste", "Üretim Fire", "Uretim_Fire"],
                  ["bmr", "Üretim Dosyası / BMR", "Uretim_Dosyasi"],
                ].map(([path, label, fname]) => (
                  <button key={path}
                          data-testid={`prod-doc-${path}`}
                          className="w-full text-left px-3 py-2 hover:bg-indigo-50 text-sm"
                          onClick={async () => {
                            try {
                              const backendUrl = process.env.REACT_APP_BACKEND_URL;
                              const token = localStorage.getItem("erp_token");
                              const resp = await fetch(`${backendUrl}/api/production/${id}/${path}/pdf`,
                                { headers: { Authorization: `Bearer ${token}` } });
                              if (!resp.ok) { alert(`Doküman açılamadı (${resp.status})`); return; }
                              const blob = await resp.blob();
                              const { openPdfPreview } = await import("../lib/pdfPreview");
                              openPdfPreview(blob, `${fname}_${po.prod_no}.pdf`);
                            } catch (e) { alert("Doküman açılamadı: " + (e.message || e)); }
                          }}>
                    {label}
                  </button>
                ))}
              </div>
            </details>
            {po.customer_id && (
              <TelegramSendButton
                customer_id={po.customer_id}
                kind="production_status"
                entity_ref={po.prod_no}
                label="Üretim Durumu"
                size="md"
                defaultMessage={`🏭 Üretim Bilgilendirmesi\n\nSipariş: ${po.prod_no}\nDurum: ${PROD_STATUS_LABEL[po.status] || po.status}\nPlanlanan: ${po.planned_units || 0} adet\nÜretilen: ${po.total_filled_units || 0} adet\n${po.actual_bulk_kg ? `Bulk: ${po.actual_bulk_kg} kg\n` : ""}${po.status === "completed" ? "\n✅ Üretim tamamlandı, kalite kontrol süreci tamamlandığında sevkiyat planlanacak." : "\nGüncel durum yukarıdadır. Herhangi bir sorunuz için lütfen bizimle iletişime geçin."}\n\n— Meta Pharma`}
                testid="tg-production-status"
              />
            )}
            {po.status === "in_production" && <Btn onClick={() => setBulkOpen(true)} data-testid="complete-bulk"><Beaker size={14}/> Bulk Tamamla</Btn>}
            {(po.status === "ready_for_filling" || po.status === "awaiting_qc_finished") && <Btn onClick={() => setFillOpen(true)} data-testid="complete-filling"><Factory size={14}/> Dolum Yap</Btn>}
            {(po.filling_operations || []).length > 0 && po.status !== "completed" && po.status !== "cancelled" && <Btn variant="secondary" onClick={async () => { if (window.confirm("Üretim tamamen bitirilsin mi? Sonraki dolum yapılamayacak.")) { try { await api.post(`/production-orders/${id}/complete`); load(); } catch (e) { alert(e.response?.data?.detail); } } }} data-testid="confirm-complete"><CheckCircle size={14}/> Üretimi Onayla</Btn>}
          </>
        } />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4" data-testid="prod-summary-strip">
        {/* Iter244 (Faz 3C) — Display-only unit selector for Hammadde masses.
            Canonical kg stays in DB; only the on-screen numbers and the PDF
            download URL flip between kg <-> g. adet is never converted. */}
        <div className="col-span-2 sm:col-span-4 flex items-center gap-2 text-xs">
          <span className="uppercase font-semibold text-slate-500">Görüntüleme Birimi:</span>
          <select
            value={prodUnit}
            onChange={e => { setProdUnit(e.target.value); try { localStorage.setItem("prod_display_unit", e.target.value); } catch {} }}
            className="border rounded px-2 py-0.5 text-xs"
            data-testid="prod-display-unit">
            <option value="kg">kg</option>
            <option value="g">g</option>
          </select>
          <span className="text-[10px] text-slate-400 italic">(yalnız görüntüleme — canonical veri kg olarak saklanır)</span>
        </div>
        <Card className="p-3 text-sm"><div className="text-xs uppercase text-slate-500 mb-1">Planlanan Batch</div><span data-testid="prod-planned-batch">{fmtMass(po.planned_batch_kg)}</span></Card>
        <Card className="p-3 text-sm"><div className="text-xs uppercase text-slate-500 mb-1">Planlanan Adet</div>{po.planned_units}</Card>
        <Card className="p-3 text-sm"><div className="text-xs uppercase text-slate-500 mb-1">Gerçekleşen Bulk</div><span data-testid="prod-actual-bulk">{fmtMass(po.actual_bulk_kg || 0)}</span></Card>
        <Card className="p-3 text-sm"><div className="text-xs uppercase text-slate-500 mb-1">Kalan Bulk</div><span data-testid="prod-remaining-bulk">{po.remaining_bulk_kg != null ? fmtMass(po.remaining_bulk_kg) : "—"}</span></Card>
      </div>
      {po.cost_snapshot && isAdmin && (
        <Card className="p-3 mb-4 bg-indigo-50/40" data-testid="prod-cost-snapshot">
          <div className="text-xs font-semibold uppercase text-indigo-700 mb-2">Maliyet Anlık Görüntüsü <span className="text-[9px] text-slate-500 italic">· Yalnızca Sistem Yöneticisi</span></div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
            <div>Ham Madde: <span className="font-mono font-semibold">{po.cost_snapshot.raw_cost} EUR</span></div>
            <div>Ambalaj: <span className="font-mono font-semibold">{po.cost_snapshot.pkg_cost} EUR</span></div>
            <div>Toplam: <span className="font-mono font-semibold">{po.cost_snapshot.total_cost} EUR</span></div>
            <div>Adet Başına: <span className="font-mono font-semibold">{po.cost_snapshot.per_unit} EUR</span></div>
          </div>
        </Card>
      )}
      {/* Section order per spec msg-175 rules 21-35:
          1. Sipariş/Üretim Bilgileri (header above)
          2. Tartım Listesi (raw materials + FIFO lots)  — moved above QC
          3. Ambalaj Bilgileri                            — right after Tartım
          4. Kalite Kontrol                               — after Ambalaj
          5. Dolum Bilgileri                              — after QC
          6. Fire / Üretim Sonuç
          7. Karlılık (admin only)
          8. Onaylar
       */}

      {/* 4. QC Bilgileri — Referans (Formülden) + Kayıtlı Değerler
          NOTE: Tartım Listesi rendering block lives further below; this QC card
          is repositioned there via the anchor comments so the DOM order matches. */}
      <div id="qc-anchor" style={{ order: 20 }} />
      {/* Iter 204 §17 — Equipment Usage panel: real usage events per production order. */}
      <div style={{ order: 15 }} className="mb-4">
        <EquipmentUsagePanel productionOrderId={id} prodNo={po.prod_no}/>
        <EquipmentCleaningPanel productionOrderId={id} prodNo={po.prod_no}/>
      </div>
      <Card className="p-3 mb-4 bg-cyan-50/40 border-cyan-200" data-testid="qc-info-card" style={{ order: 20 }}>
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-semibold uppercase text-cyan-800 flex items-center gap-1">
            <ShieldCheck size={12}/> QC Bilgileri, Alt/Üst Limit & Ölçüm Sonuçları
            {po.production_qc_id && <span className="ml-2 text-[10px] text-slate-500 font-mono">QC No: {po.prod_no}</span>}
          </div>
          <Btn size="sm" variant="secondary" data-testid="qc-batch-pdf" title="QC Raporu PDF" onClick={async () => {
            try {
              const blob = await api.getBlob(`/production-orders/${id}/qc-batch-pdf`);
              openPdfPreview(blob, `QC-Dossier-${po.prod_no}.pdf`);
            } catch (e) { alert(e.response?.data?.detail || "QC kaydı yok"); }
          }}><FileText size={12}/></Btn>
        </div>

        {/* Referans QC Değerleri — Formüller sayfasından */}
        {(po.formula_qc_specs && po.formula_qc_specs.length > 0) ? (
          <div className="mb-3">
            <div className="text-[10px] uppercase text-indigo-700 font-semibold mb-1">📋 Referans QC Değerleri (Formülden)</div>
            <table className="w-full text-xs mt-1 border border-indigo-200 rounded overflow-hidden" data-testid="qc-reference-table">
              <thead className="bg-indigo-100"><tr>
                <th className="text-left px-2 py-1">Parametre</th>
                <th className="px-2 py-1">Alt Limit</th>
                <th className="px-2 py-1">Üst Limit</th>
                <th className="px-2 py-1">Birim / Beklenen</th>
                <th className="px-2 py-1">Ölçülen (QC Kaydı)</th>
              </tr></thead>
              <tbody>{po.formula_qc_specs.map((s, i) => {
                // Look up recorded measurement from bulk_qc or batch_qc by parameter name
                const findMeasured = (qc) => (qc?.specs || []).find(x => (x.name || "").toLowerCase() === (s.name || "").toLowerCase());
                const bulkM = findMeasured(po.bulk_qc);
                const batchM = findMeasured(po.batch_qc);
                const measuredParts = [];
                if (bulkM) measuredParts.push(`Bulk: ${bulkM.measured ?? bulkM.text_result ?? "—"}${bulkM.passed === false ? " ✗" : bulkM.passed === true ? " ✓" : ""}`);
                if (batchM) measuredParts.push(`Batch: ${batchM.measured ?? batchM.text_result ?? "—"}${batchM.passed === false ? " ✗" : batchM.passed === true ? " ✓" : ""}`);
                return (
                <tr key={i} className="border-t border-indigo-100 bg-white">
                  <td className="px-2 py-1 font-semibold">{s.name}</td>
                  <td className="px-2 py-1 text-center font-mono">{s.lower ?? "—"}</td>
                  <td className="px-2 py-1 text-center font-mono">{s.upper ?? "—"}</td>
                  <td className="px-2 py-1 text-center text-slate-600">{s.type === "text" ? (s.expected_text || "—") : (s.unit || "—")}</td>
                  <td className="px-2 py-1 text-center font-mono text-emerald-700 font-semibold" data-testid={`qc-measured-${i}`}>
                    {measuredParts.length > 0 ? measuredParts.map((m, mi) => <div key={mi}>{m}</div>) : <span className="text-slate-300">Henüz ölçüm yok</span>}
                  </td>
                </tr>
              );})}</tbody>
            </table>
          </div>
        ) : (
          <div className="text-xs text-slate-500 italic mb-3">Bu formül için referans QC değeri tanımlanmamış (Formüller sayfasından ekleyin).</div>
        )}

        {/* Bulk QC — Kaydedilmiş değerler */}
        {po.bulk_qc && (
          <div className="mb-3">
            <div className="text-[10px] uppercase text-cyan-700 font-semibold mb-1">🧪 Bulk QC — Kaydedilmiş Değerler</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs mb-1">
              <div><span className="text-slate-500">Sonuç:</span> <Badge level={po.bulk_qc.result === "pass" ? "ok" : "missing"}>{po.bulk_qc.result === "pass" ? "Uygun ✓" : po.bulk_qc.result === "fail" ? "Uygun Değil ✗" : "Bekliyor"}</Badge></div>
              <div><span className="text-slate-500">Tarih:</span> <span className="font-mono">{(po.bulk_qc.tested_at || "").slice(0, 16).replace("T", " ") || "—"}</span></div>
              <div><span className="text-slate-500">Test Eden:</span> <span>{po.bulk_qc.tested_by_name || "—"}</span></div>
              <div><span className="text-slate-500">Onaylayan:</span> <span>{po.bulk_qc.approved_by_name || "—"}</span></div>
            </div>
            {po.bulk_qc.specs && po.bulk_qc.specs.length > 0 && (
              <table className="w-full text-xs mt-1 border border-cyan-200 rounded overflow-hidden" data-testid="qc-bulk-table">
                <thead className="bg-cyan-100"><tr><th className="text-left px-2 py-1">Parametre</th><th className="px-2 py-1">Referans</th><th className="px-2 py-1">Ölçülen</th><th className="px-2 py-1">Durum</th></tr></thead>
                <tbody>{po.bulk_qc.specs.map((s, i) => (
                  <tr key={i} className="border-t border-cyan-100">
                    <td className="px-2 py-1">{s.name}{s.unit ? ` (${s.unit})` : ""}</td>
                    <td className="px-2 py-1 text-center font-mono text-[10px]">{s.lower != null ? `${s.lower}—${s.upper}` : (s.expected_text || "—")}</td>
                    <td className="px-2 py-1 text-center font-mono font-semibold">{s.measured ?? s.text_result ?? s.value ?? "—"}</td>
                    <td className="px-2 py-1 text-center">{s.passed === true ? <span className="text-emerald-600">✓</span> : s.passed === false ? <span className="text-red-600">✗</span> : "—"}</td>
                  </tr>
                ))}</tbody>
              </table>
            )}
            {po.bulk_qc.notes && <div className="text-xs italic text-slate-600 mt-1">Not: {po.bulk_qc.notes}</div>}
          </div>
        )}

        {/* Batch (Bitmiş Ürün) QC — Kaydedilmiş değerler + specs tablosu */}
        {po.batch_qc && (
          <div className="mb-3">
            <div className="text-[10px] uppercase text-emerald-700 font-semibold mb-1">📦 Batch (Bitmiş Ürün) QC — Kaydedilmiş Değerler</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs mb-1">
              <div><span className="text-slate-500">Sonuç:</span> <Badge level={po.batch_qc.result === "pass" ? "ok" : "missing"}>{po.batch_qc.result === "pass" ? "Uygun ✓" : po.batch_qc.result === "fail" ? "Uygun Değil ✗" : "Bekliyor"}</Badge></div>
              <div><span className="text-slate-500">Tarih:</span> <span className="font-mono">{(po.batch_qc.tested_at || "").slice(0, 16).replace("T", " ") || "—"}</span></div>
              <div><span className="text-slate-500">Test Eden:</span> <span>{po.batch_qc.tested_by_name || "—"}</span></div>
              <div><span className="text-slate-500">Onaylayan:</span> <span>{po.batch_qc.approved_by_name || "—"}</span></div>
            </div>
            {po.batch_qc.specs && po.batch_qc.specs.length > 0 && (
              <table className="w-full text-xs mt-1 border border-emerald-200 rounded overflow-hidden" data-testid="qc-batch-table">
                <thead className="bg-emerald-100"><tr><th className="text-left px-2 py-1">Parametre</th><th className="px-2 py-1">Referans</th><th className="px-2 py-1">Ölçülen</th><th className="px-2 py-1">Durum</th></tr></thead>
                <tbody>{po.batch_qc.specs.map((s, i) => (
                  <tr key={i} className="border-t border-emerald-100">
                    <td className="px-2 py-1">{s.name}{s.unit ? ` (${s.unit})` : ""}</td>
                    <td className="px-2 py-1 text-center font-mono text-[10px]">{s.lower != null ? `${s.lower}—${s.upper}` : (s.expected_text || "—")}</td>
                    <td className="px-2 py-1 text-center font-mono font-semibold">{s.measured ?? s.text_result ?? s.value ?? "—"}</td>
                    <td className="px-2 py-1 text-center">{s.passed === true ? <span className="text-emerald-600">✓</span> : s.passed === false ? <span className="text-red-600">✗</span> : "—"}</td>
                  </tr>
                ))}</tbody>
              </table>
            )}
            {po.batch_qc.notes && <div className="text-xs italic text-slate-600 mt-1">Not: {po.batch_qc.notes}</div>}
          </div>
        )}

        {po.production_qc_approved_at && !po.bulk_qc && (
          <div className="text-xs text-slate-600">
            <b>Üretim QC:</b> {po.production_qc_approved_by_name || "—"} tarafından {(po.production_qc_approved_at || "").slice(0, 16).replace("T", " ")} tarihinde onaylandı.
          </div>
        )}
        {!po.bulk_qc && !po.batch_qc && !po.production_qc_approved_at && (
          <div className="text-xs text-slate-500 italic">Henüz QC kaydı yok. Üretim tamamlandığında Kalite Kontrol sayfasından ölçüm değerlerini girin.</div>
        )}
      </Card>

      {/* 5. Dolum Bilgileri (moved AFTER QC per spec rule 30) */}
      {po.filling_operations && po.filling_operations.length > 0 && (
        <Card className="p-3 mb-4" data-testid="filling-ops-card" style={{ order: 30 }}>
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold uppercase text-slate-600">Dolum Bilgileri ({po.total_filled_units} adet)</div>
            {/* Iter 202 §6 — Filling traceability trigger (Operator / Started / Completed). */}
            <Btn size="sm" variant="ghost" onClick={openTraceability} data-testid="open-traceability-filling">
              <ShieldCheck size={12}/> İzlenebilirlik
            </Btn>
          </div>
          <table className="w-full text-sm">
            <thead><tr className="border-b"><th className="text-left px-2 py-1 text-xs">Sıra</th><th className="text-left px-2 py-1 text-xs">Tarih</th><th className="text-left px-2 py-1 text-xs">Operatör</th><th className="text-left px-2 py-1 text-xs">Adet</th><th className="text-left px-2 py-1 text-xs">Bulk (kg)</th><th className="text-left px-2 py-1 text-xs">Lot</th><th className="text-left px-2 py-1 text-xs">Başlangıç/Bitiş</th></tr></thead>
            <tbody>{po.filling_operations.map((op, i) => (
              <tr key={i} className="border-b border-slate-100" data-testid={`filling-row-${i}`}>
                <td className="px-2 py-1">#{op.index + 1}</td>
                <td className="px-2 py-1">{(op.filled_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="px-2 py-1">{op.operator_name}</td>
                <td className="px-2 py-1 font-mono">{op.accepted_units}</td>
                <td className="px-2 py-1 font-mono">{op.bulk_consumed_kg}</td>
                <td className="px-2 py-1 font-mono text-xs">{op.finished_lot_code}</td>
                <td className="px-2 py-1 text-[10px] text-slate-500" data-testid={`filling-trace-${i}`}>
                  {op.started_at ? <div>Başl: {op.started_at.slice(0, 16).replace("T", " ")}</div> : null}
                  {op.completed_at ? <div>Bitiş: {op.completed_at.slice(0, 16).replace("T", " ")}</div> : null}
                  {!op.started_at && !op.completed_at ? <span className="italic">—</span> : null}
                </td>
              </tr>
            ))}</tbody>
          </table>
        </Card>
      )}

      <div className="flex items-center justify-between mb-2" style={{ order: 10 }} data-testid="weighing-header">
        <h2 className="text-base font-semibold" style={{ fontFamily: "'Manrope', sans-serif" }}>Tartım Listesi</h2>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-slate-500 italic">Bir malzeme birden fazla lottan çekiliyorsa, lotlar aynı malzeme adı altında alt alta listelenir.</span>
          {/* Iter 202 §5 — Traceability button (Tartan / Kontrol Eden / Timestamps). §9: single weighing metric only. */}
          <Btn size="sm" variant="secondary" onClick={openTraceability} data-testid="open-traceability">
            <ShieldCheck size={12}/> İzlenebilirlik
          </Btn>
        </div>
      </div>
      <div className="overflow-x-auto bg-white border border-slate-200 rounded-lg" style={{ order: 10 }}>
        <table className="w-full text-sm" data-testid="weighing-table">
          <thead><tr className="border-b border-slate-200 bg-slate-50/50">
            {["Faz", "Malzeme", "Teorik", "Fire %", "Lot", "Gerçek", "Sapma", "İşlem"].map((h, i) => <th key={i} className="text-left px-3 py-2 text-xs font-semibold text-slate-600 uppercase">{h}</th>)}
          </tr></thead>
          <tbody>
            {(() => {
              // Group weighing rows by material_id so multi-lot FIFO splits stack under one material row.
              const groups = new Map();
              (po.weighing || []).forEach((w, i) => {
                const key = w.material_id || `_${i}`;
                if (!groups.has(key)) groups.set(key, []);
                groups.get(key).push({ ...w, _origIndex: i });
              });
              const flat = [];
              for (const [, rows] of groups) {
                rows.forEach((w, subIdx) => flat.push({ w, subIdx, groupSize: rows.length }));
              }
              return flat.map(({ w, subIdx, groupSize }, flatIdx) => {
                const i = w._origIndex;
                const availLots = lots.filter(l => l.material_id === w.material_id);
                const isFirstInGroup = subIdx === 0;
                const isSplit = groupSize > 1;
                return (
                  <tr key={flatIdx} className={`border-b border-slate-100 ${isSplit ? "bg-amber-50/30" : ""} ${!isFirstInGroup ? "border-t-0" : ""}`} data-testid={`weigh-row-${i}`}>
                    <td className="px-3 py-2">{isFirstInGroup ? w.phase : ""}</td>
                    <td className="px-3 py-2">
                      {isFirstInGroup ? (
                        <span className="font-semibold">{mname(w.material_id)}</span>
                      ) : (
                        <span className="text-slate-400 text-xs pl-3">↳ ek lot</span>
                      )}
                      {isFirstInGroup && isSplit && (
                        <span className="ml-2 text-[9px] px-1.5 py-0.5 bg-amber-200 text-amber-800 rounded font-semibold" title={`${groupSize} lottan çekilecek`} data-testid={`multi-lot-${i}`}>
                          {groupSize} LOT
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono">{w.theoretical_qty} {w.unit}</td>
                    <td className="px-3 py-2">{isFirstInGroup ? `${w.tolerance_pct || 2}%` : ""}</td>
                    <td className="px-3 py-2">
                      {w.lot_id ? (() => {
                        const lotRec = lots.find(l => l.id === w.lot_id);
                        const exp = w.lot_expiry_date || lotRec?.expiry_date;
                        const mfg = w.lot_mfg_date || lotRec?.mfg_date;
                        let warn = false;
                        let daysLeft = null;
                        if (exp) {
                          const now = new Date();
                          const expDate = new Date(exp);
                          daysLeft = Math.ceil((expDate - now) / 86400000);
                          if (mfg) {
                            const total = Math.ceil((expDate - new Date(mfg)) / 86400000);
                            if (total > 0 && daysLeft / total < 0.20) warn = true;
                          } else if (daysLeft < 60) { warn = true; }
                        }
                        return (
                          <div className={`flex items-center gap-1 ${warn ? "bg-rose-50 border border-rose-300 rounded px-1.5 py-1" : ""}`}>
                            <span className="font-mono text-xs">{w.lot_code || lotRec?.lot_code || w.lot_id.slice(0,8)}</span>
                            {w.auto_assigned && <span className="text-[9px] px-1 py-0.5 bg-teal-100 text-teal-700 rounded font-semibold" title="Otomatik FIFO ile atandı">FIFO</span>}
                            {w.split_extra && <span className="text-[9px] px-1 py-0.5 bg-amber-200 text-amber-900 rounded font-semibold" title="Bu satır FIFO bölünmesinden geldi (aynı malzemede önceki lot yetmedi)">SPLIT</span>}
                            {warn && <span className="text-[9px] px-1 py-0.5 bg-rose-600 text-white rounded font-semibold" title={`SKT'ye ${daysLeft} gün kaldı — %20 tampon altında. Öncelikli olarak tartın.`} data-testid={`fifo-warn-${i}`}>⚠ SKT&lt;20%</span>}
                            {!w.actual_qty && <WeighRow index={i} row={w} availLots={availLots} onWeigh={weigh} preAssigned={w.lot_id}/>}
                          </div>
                        );
                      })() : <WeighRow index={i} row={w} availLots={availLots} onWeigh={weigh} preAssigned=""/>}
                    </td>
                    <td className="px-3 py-2 font-mono">{w.actual_qty || "—"}</td>
                    <td className="px-3 py-2 font-mono">{w.variance ? (w.variance > 0 ? "+" : "") + w.variance : "—"}</td>
                    <td className="px-3 py-2">
                      {w.lot_id && w.actual_qty ? <Badge level="ok">Tartıldı</Badge> : null}
                      {/* Iter 202 §5 — inline traceability chips (existing values, read-only summary). */}
                      {(w.weighed_by_name || w.checked_by_name) && (
                        <div className="mt-1 text-[9px] text-slate-500 leading-tight" data-testid={`trace-summary-${i}`}>
                          {w.weighed_by_name && <div>Tartan: <b className="text-slate-700">{w.weighed_by_name}</b>{w.weighed_at ? ` · ${w.weighed_at.slice(0, 16).replace("T", " ")}` : ""}</div>}
                          {w.checked_by_name && <div>Kontrol: <b className="text-slate-700">{w.checked_by_name}</b>{w.checked_at ? ` · ${w.checked_at.slice(0, 16).replace("T", " ")}` : ""}</div>}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              });
            })()}
          </tbody>
        </table>
      </div>

      <Modal open={bulkOpen} onClose={() => setBulkOpen(false)} title="Bulk Üretimi Tamamla">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input label="Gerçek Bulk (kg) *" type="number" step="0.01" value={bulkData.actual_bulk_kg} onChange={e => setBulkData({ ...bulkData, actual_bulk_kg: parseFloat(e.target.value) })} data-testid="actual-bulk"/>
          <Input label="Numune (kg)" type="number" step="0.01" value={bulkData.sample_kg} onChange={e => setBulkData({ ...bulkData, sample_kg: parseFloat(e.target.value) })} />
          <Input label="Fire (kg)" type="number" step="0.01" value={bulkData.waste_kg} onChange={e => setBulkData({ ...bulkData, waste_kg: parseFloat(e.target.value) })} />
        </div>
        <div className="flex justify-end gap-2 mt-4"><Btn variant="secondary" onClick={() => setBulkOpen(false)}>Vazgeç</Btn><Btn onClick={completeBulk} data-testid="save-bulk">Kaydet ve QC'ye Gönder</Btn></div>
      </Modal>

      <Modal open={fillOpen} onClose={() => setFillOpen(false)} title="Dolum Tamamla">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input label="Kabul Edilen Adet *" type="number" value={fillData.accepted_units} onChange={e => setFillData({ ...fillData, accepted_units: parseInt(e.target.value) })} data-testid="accepted-units"/>
          <Input label="QC Numune" type="number" value={fillData.qc_samples} onChange={e => setFillData({ ...fillData, qc_samples: parseInt(e.target.value) })} />
          <Input label="Kusurlu Adet" type="number" value={fillData.defective_units} onChange={e => setFillData({ ...fillData, defective_units: parseInt(e.target.value) })} />
          <Input label="Ambalaj Firesi" type="number" value={fillData.packaging_waste} onChange={e => setFillData({ ...fillData, packaging_waste: parseInt(e.target.value) })} />
          <Input label="Kalan Bulk (kg)" type="number" step="0.01" value={fillData.remaining_bulk_kg} onChange={e => setFillData({ ...fillData, remaining_bulk_kg: parseFloat(e.target.value) })} />
        </div>
        <div className="flex justify-end gap-2 mt-4"><Btn variant="secondary" onClick={() => setFillOpen(false)} disabled={fillSubmitting}>Vazgeç</Btn><Btn onClick={completeFilling} disabled={fillSubmitting} data-testid="save-filling">{fillSubmitting ? "Kaydediliyor…" : "Kaydet"}</Btn></div>
      </Modal>

      {/* Iter 202 §5–8 — Traceability modal (Tartan / Kontrol Eden / Filling / Packaging).
          §9: single weighing metric only. §10: FIFO unchanged. Uses existing PATCH endpoint (Iter 201). */}
      <Modal open={traceOpen} onClose={() => setTraceOpen(false)} title="Üretim İzlenebilirliği" size="lg">
        <div className="text-xs bg-sky-50 border border-sky-200 rounded p-2 mb-3 text-sky-900" data-testid="trace-modal-info">
          Tartan / Kontrol Eden / Zaman damgaları GMP izlenebilirlik için kayıt edilir. <b>Tartılacak miktar ve FIFO lot ataması değişmez</b> (§9, §10). "Beni Ata" butonu mevcut oturum kullanıcısını atar.
        </div>
        {traceDraft.weighing.length > 0 && (
          <div className="mb-4">
            <div className="text-xs font-semibold uppercase text-slate-600 mb-1">Hammadde Tartım</div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border border-slate-200 rounded" data-testid="trace-weighing-table">
                <thead className="bg-slate-50 text-slate-600"><tr>
                  <th className="text-left px-2 py-1">Faz</th>
                  <th className="text-left px-2 py-1">Malzeme</th>
                  <th className="text-left px-2 py-1">Tartan</th>
                  <th className="text-left px-2 py-1">Tartım Tarihi</th>
                  <th className="text-left px-2 py-1">Kontrol Eden</th>
                  <th className="text-left px-2 py-1">Kontrol Tarihi</th>
                </tr></thead>
                <tbody>{traceDraft.weighing.map((w, i) => (
                  <tr key={i} className="border-t border-slate-100" data-testid={`trace-weigh-row-${i}`}>
                    <td className="px-2 py-1">{w.phase}</td>
                    <td className="px-2 py-1">{w.material_name}</td>
                    <td className="px-2 py-1">
                      <div className="flex gap-1 items-center">
                        <input value={w.weighed_by_name || ""} placeholder="Tartan"
                          onChange={e => { const d = { ...traceDraft }; d.weighing[i] = { ...d.weighing[i], weighed_by_name: e.target.value }; setTraceDraft(d); }}
                          className="px-1 py-0.5 border rounded w-32 text-[11px]" data-testid={`trace-weighed-name-${i}`}/>
                        <button type="button" onClick={() => assignMeToWeighing(i, "weighed_by")}
                          className="text-[9px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded border border-indigo-200"
                          data-testid={`trace-weighed-me-${i}`}>Beni Ata</button>
                      </div>
                    </td>
                    <td className="px-2 py-1">
                      <input type="datetime-local" value={(w.weighed_at || "").slice(0, 16)}
                        onChange={e => { const d = { ...traceDraft }; d.weighing[i] = { ...d.weighing[i], weighed_at: e.target.value ? new Date(e.target.value).toISOString() : "" }; setTraceDraft(d); }}
                        className="px-1 py-0.5 border rounded text-[11px]" data-testid={`trace-weighed-at-${i}`}/>
                    </td>
                    <td className="px-2 py-1">
                      <div className="flex gap-1 items-center">
                        <input value={w.checked_by_name || ""} placeholder="Kontrol"
                          onChange={e => { const d = { ...traceDraft }; d.weighing[i] = { ...d.weighing[i], checked_by_name: e.target.value }; setTraceDraft(d); }}
                          className="px-1 py-0.5 border rounded w-32 text-[11px]" data-testid={`trace-checked-name-${i}`}/>
                        <button type="button" onClick={() => assignMeToWeighing(i, "checked_by")}
                          className="text-[9px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded border border-indigo-200"
                          data-testid={`trace-checked-me-${i}`}>Beni Ata</button>
                      </div>
                    </td>
                    <td className="px-2 py-1">
                      <input type="datetime-local" value={(w.checked_at || "").slice(0, 16)}
                        onChange={e => { const d = { ...traceDraft }; d.weighing[i] = { ...d.weighing[i], checked_at: e.target.value ? new Date(e.target.value).toISOString() : "" }; setTraceDraft(d); }}
                        className="px-1 py-0.5 border rounded text-[11px]" data-testid={`trace-checked-at-${i}`}/>
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        )}
        {traceDraft.filling.length > 0 && (
          <div className="mb-4">
            <div className="text-xs font-semibold uppercase text-slate-600 mb-1">Dolum Operasyonları</div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border border-slate-200 rounded" data-testid="trace-filling-table">
                <thead className="bg-slate-50 text-slate-600"><tr>
                  <th className="text-left px-2 py-1">Sıra</th>
                  <th className="text-left px-2 py-1">Operatör</th>
                  <th className="text-left px-2 py-1">Başlangıç</th>
                  <th className="text-left px-2 py-1">Bitiş</th>
                </tr></thead>
                <tbody>{traceDraft.filling.map((f, i) => (
                  <tr key={i} className="border-t border-slate-100" data-testid={`trace-fill-row-${i}`}>
                    <td className="px-2 py-1">#{i + 1}</td>
                    <td className="px-2 py-1">
                      <div className="flex gap-1 items-center">
                        <input value={f.operator_name || ""} placeholder="Operatör"
                          onChange={e => { const d = { ...traceDraft }; d.filling[i] = { ...d.filling[i], operator_name: e.target.value }; setTraceDraft(d); }}
                          className="px-1 py-0.5 border rounded w-32 text-[11px]" data-testid={`trace-fill-op-${i}`}/>
                        <button type="button" onClick={() => assignMeToFilling(i, "operator")}
                          className="text-[9px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded border border-indigo-200"
                          data-testid={`trace-fill-me-${i}`}>Beni Ata</button>
                      </div>
                    </td>
                    <td className="px-2 py-1">
                      <input type="datetime-local" value={(f.started_at || "").slice(0, 16)}
                        onChange={e => { const d = { ...traceDraft }; d.filling[i] = { ...d.filling[i], started_at: e.target.value ? new Date(e.target.value).toISOString() : "" }; setTraceDraft(d); }}
                        className="px-1 py-0.5 border rounded text-[11px]" data-testid={`trace-fill-start-${i}`}/>
                    </td>
                    <td className="px-2 py-1">
                      <input type="datetime-local" value={(f.completed_at || "").slice(0, 16)}
                        onChange={e => { const d = { ...traceDraft }; d.filling[i] = { ...d.filling[i], completed_at: e.target.value ? new Date(e.target.value).toISOString() : "" }; setTraceDraft(d); }}
                        className="px-1 py-0.5 border rounded text-[11px]" data-testid={`trace-fill-end-${i}`}/>
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        )}
        {traceDraft.packaging.length > 0 && (
          <div className="mb-4">
            <div className="text-xs font-semibold uppercase text-slate-600 mb-1">Ambalaj Malzeme Tüketimi</div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border border-slate-200 rounded" data-testid="trace-packaging-table">
                <thead className="bg-slate-50 text-slate-600"><tr>
                  <th className="text-left px-2 py-1">Malzeme</th>
                  <th className="text-left px-2 py-1">Operatör</th>
                  <th className="text-left px-2 py-1">Tüketim Zamanı</th>
                </tr></thead>
                <tbody>{traceDraft.packaging.map((p, i) => (
                  <tr key={i} className="border-t border-slate-100" data-testid={`trace-pkg-row-${i}`}>
                    <td className="px-2 py-1">{p.material_name}</td>
                    <td className="px-2 py-1">
                      <div className="flex gap-1 items-center">
                        <input value={p.operator_name || ""} placeholder="Operatör"
                          onChange={e => { const d = { ...traceDraft }; d.packaging[i] = { ...d.packaging[i], operator_name: e.target.value }; setTraceDraft(d); }}
                          className="px-1 py-0.5 border rounded w-32 text-[11px]" data-testid={`trace-pkg-op-${i}`}/>
                        <button type="button" onClick={() => {
                          const d = { ...traceDraft };
                          d.packaging[i] = { ...d.packaging[i], operator_name: user?.name || user?.username || "unknown" };
                          setTraceDraft(d);
                        }} className="text-[9px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded border border-indigo-200"
                          data-testid={`trace-pkg-me-${i}`}>Beni Ata</button>
                      </div>
                    </td>
                    <td className="px-2 py-1">
                      <input type="datetime-local" value={(p.consumed_at || "").slice(0, 16)}
                        onChange={e => { const d = { ...traceDraft }; d.packaging[i] = { ...d.packaging[i], consumed_at: e.target.value ? new Date(e.target.value).toISOString() : "" }; setTraceDraft(d); }}
                        className="px-1 py-0.5 border rounded text-[11px]" data-testid={`trace-pkg-consumed-${i}`}/>
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        )}
        {traceDraft.weighing.length === 0 && traceDraft.filling.length === 0 && traceDraft.packaging.length === 0 && (
          <div className="text-xs text-slate-500 italic p-4">İzlenebilirlik kaydı için henüz veri yok. Üretim akışı başlatıldığında bu form dolar.</div>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <Btn variant="secondary" onClick={() => setTraceOpen(false)} data-testid="trace-cancel">Vazgeç</Btn>
          <Btn onClick={saveTraceability} data-testid="trace-save"><ShieldCheck size={12}/> Kaydet</Btn>
        </div>
      </Modal>

      {/* iter268 — Detay sayfasında eksik malzeme bilgi modalı — YALNIZCA BİLGİ AMAÇLIDIR */}
      <Modal
        open={!!detailShortageModal}
        onClose={() => setDetailShortageModal(null)}
        title={`Eksik Malzemeler — ${detailShortageModal?.prodNo || ""}`}
      >
        <div className="text-[11px] bg-amber-50 border border-amber-200 rounded p-2 mb-3 text-amber-800 flex items-start gap-2" data-testid="detail-shortage-modal-info">
          <AlertTriangle size={14} className="mt-0.5 flex-shrink-0"/>
          <div>
            Bu ekran <b>yalnızca bilgi amaçlıdır</b>. Üretim onayından önce görünür — eksik hammadde/ambalaj varsa üretime başlamadan önce tedarik edilmelidir. Hiçbir işlemi engellemez.
          </div>
        </div>
        {(detailShortageModal?.items || []).length === 0 ? (
          <div className="text-xs italic text-slate-400 py-4 text-center">Eksik yok.</div>
        ) : (
          <div className="max-h-96 overflow-y-auto border border-slate-200 rounded" data-testid="detail-shortage-modal-list">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600">Malzeme Adı</th>
                  <th className="text-right px-3 py-2 font-semibold text-slate-600 whitespace-nowrap">Eksik Miktar</th>
                </tr>
              </thead>
              <tbody>
                {detailShortageModal.items.map((it, i) => (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`detail-shortage-modal-row-${i}`}>
                    <td className="px-3 py-2 font-medium text-slate-800">{it.name}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-amber-700 whitespace-nowrap">{it.shortage} {it.unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex justify-end mt-4">
          <Btn variant="secondary" onClick={() => setDetailShortageModal(null)} data-testid="detail-shortage-modal-close">Kapat</Btn>
        </div>
      </Modal>
    </div>
  );
}

function WeighRow({ index, row, availLots, onWeigh, preAssigned }) {
  const [lot, setLot] = useState(preAssigned || "");
  const [qty, setQty] = useState(row.theoretical_qty);
  return (
    <div className="flex gap-1 items-center">
      <select value={lot} onChange={e => setLot(e.target.value)} className="px-1 py-0.5 text-xs border rounded" data-testid={`weigh-lot-${index}`}>
        <option value="">Lot seç...</option>
        {availLots.map(l => <option key={l.id} value={l.id}>{l.lot_code} ({l.qty_available})</option>)}
      </select>
      <input type="number" step="0.001" value={qty} onChange={e => setQty(e.target.value)} className="w-20 px-1 py-0.5 text-xs border rounded" data-testid={`weigh-qty-${index}`}/>
      <button onClick={() => lot && onWeigh(index, lot, qty)} className="text-xs bg-indigo-600 text-white px-2 py-0.5 rounded" data-testid={`weigh-save-${index}`}>Tart</button>
    </div>
  );
}
